import * as React from 'react';
import { dateFormater, ddMMyyyyParse, getBoolean } from '@app/utils/functions';
import { 
    Button, Checkbox, CheckboxProps, DatePicker, 
    FormSelect, FormSelectOption, FormSelectOptionProps, FormSelectProps, 
    HelperText, HelperTextItem,
    TextArea, TextAreaProps, TextInput, TextInputProps 
} from '@patternfly/react-core';
import { CheckIcon, ExclamationCircleIcon, PencilAltIcon, TimesIcon } from '@patternfly/react-icons';
import { MultiTypeaheadSelect, MultiTypeaheadSelectOption, TypeaheadSelect, TypeaheadSelectOption } from '@patternfly/react-templates';
import { PhoneInput } from './PhoneInput';
import DateTimePicker from './DateTimePicker';

// FIXED: Omitted 'ref' from the base HTML props to prevent cascade issues
export interface InlineEditProps extends Omit<React.HTMLProps<HTMLInputElement>, 'disabled' | 'value' | 'label' | 'ref'> {
    label?: string;
    /** For MultiFilterableSelect this is the array of selected option values; for every
     * other type, the single value. */
    value: any;
    type: 'TextInput' | 'TextArea' | 'Checkbox' | 'Select' | 'FilterableSelect' | 'MultiFilterableSelect' | 'Telephone' | 'Date' | 'DateTime';
    displayAs?: 'Text' | 'Link';
    placeholder?: string;
    isGlobalEdition?: boolean;
    isCompact?: boolean; // NEW: Toggle for tight spaces like tables
    /** Shows the value alone, with no pencil and no way into edit mode. For records that
     * are on display rather than being worked on, such as a class outside its edit period.
     * Overrides isGlobalEdition. */
    isReadOnly?: boolean;
    /** Keeps the field where the eye expects it but takes it out of the user's hands: under
     * isGlobalEdition it is the greyed-out input, everywhere else the plain value with no
     * pencil. For a field that is filled in by something other than the user - a default that
     * is not theirs to change, a key another system assigns. */
    isDisabled?: boolean;
    id?: string;
    href?: string;
    /**
     * PatternFly's field status. 'error' puts the red outline and the exclamation icon on the
     * input - on the toggle, for the selects - and shows `errorText` underneath. The message
     * shows in display mode too: a form page spends most of its life out of edit mode, and a
     * field whose problem is only visible once its pencil is clicked may as well be silent.
     */
    validated?: 'success' | 'warning' | 'error' | 'default';
    /** What is wrong with the value, shown while `validated` is 'error'. */
    errorText?: string;
    onSave: (newValue: string | number | Date | any) => void;
    /**
     * Fired as this field's pencil opens and closes, so a form can tell that one of its fields is
     * mid-edit - a buffer that has been typed into but not confirmed, which Save would throw away
     * without ever showing it. `field` is the `id`, falling back to the label.
     *
     * Only meaningful for a field that has a pencil: under isGlobalEdition the whole form is open
     * at once and there is nothing to confirm, so nothing is reported.
     *
     * SHOULD BE STABLE across renders - a useCallback, or a function defined outside the component.
     * An inline lambda re-fires it on every render, which is harmless only while the parent ignores
     * a report that changes nothing.
     */
    onEditingChange?: (isEditing: boolean, field: string) => void;
    
    // FIXED: Omitted 'ref' from PatternFly props to solve the IntrinsicClassAttributes mismatch
    textInputProps?: Omit<TextInputProps, 'ref'>;
    textAreaProps?: Omit<TextAreaProps, 'ref'>;
    selectProps?: Omit<FormSelectProps, 'ref'>;
    checkboxProps?: Omit<CheckboxProps, 'ref'>;
    
    options?: FormSelectOptionProps[] | TypeaheadSelectOption[] | MultiTypeaheadSelectOption[];
    decorator?(value: any): React.ReactNode;
}

/** The empty buffer for a type: a list for the multi select, a blank string for the rest. */
const emptyValueFor = (type: InlineEditProps['type']) => (type === 'MultiFilterableSelect' ? [] : '');

/** MultiFilterableSelect works on arrays, which a parent rebuilds on every render. Compared
 * by content so re-seeding the edit buffer does not fire off a render of its own each time. */
const sameValue = (a: any, b: any): boolean => {
    if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((v, i) => v === b[i]);
    return a === b;
};

export const InlineEdit: React.FC<InlineEditProps> = ({
        label, value, type, onSave, placeholder = "Select an option",
        options = [], id, displayAs = 'Text', href, decorator, validated = 'default', errorText,
        onEditingChange,
        isGlobalEdition = false, isCompact = false, isReadOnly = false, isDisabled = false, ...props
}) => {
    /** The selects take their status on the toggle rather than on a `validated` prop. */
    const toggleStatus = validated === 'error' ? 'danger' as const : undefined;
    const [isEditing, setIsEditing] = React.useState(false);
    const [inputValue, setInputValue] = React.useState<any>(value ?? emptyValueFor(type));

    React.useEffect(() => {
        if (isGlobalEdition || !isEditing) {
            const next = value ?? emptyValueFor(type);
            setInputValue((current: any) => (sameValue(current, next) ? current : next));
        }
    }, [value, isGlobalEdition, isEditing, type]);

    /** Stable enough to register under even when the caller gives no id. */
    const generatedKey = React.useId();
    const editKey = id ?? label ?? generatedKey;

    /**
     * Keeps the parent's picture of which pencils are open in step with this one.
     *
     * The cleanup is what makes it safe rather than tidy: a field can be unmounted with its pencil
     * still open - a row dropped from a list, a block hidden when the checkbox above it is cleared -
     * and without it the parent would be left holding a registration for a field that no longer
     * exists, and would refuse to save for ever.
     */
    React.useEffect(() => {
        if (isGlobalEdition || isReadOnly || !onEditingChange) return;
        onEditingChange(isEditing, editKey);
        return () => { if (isEditing) onEditingChange(false, editKey); };
    }, [isEditing, isGlobalEdition, isReadOnly, editKey, onEditingChange]);

    const handleSave = () => {
        onSave(inputValue);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setInputValue(value ?? emptyValueFor(type));
        setIsEditing(false);
    };

    const handleOnBlur = (_event?: any) => {
        if (isGlobalEdition) {
            onSave(inputValue);
        }
        if (props.onBlur) {
            props.onBlur(_event);
        }
    };

    /**
     * TypeaheadSelect takes its selection from the `selected` flag of its options - it has no
     * value prop - so the flag is recomputed from the edit buffer rather than from the saved
     * value. Editing therefore opens on the value the field already holds, Cancel throws the
     * buffer away, and Confirm without touching the select is the no-op it looks like.
     *
     * Clearing is still reachable: the toggle's clear button empties the buffer through
     * onClearSelection, so a field is only blanked by asking for it.
     */
    const singleOptions = React.useMemo(() => {
        if (type !== 'FilterableSelect') return [];
        return (options as TypeaheadSelectOption[]).map(o => ({ ...o, selected: o.value === inputValue }));
    }, [type, options, inputValue]);

    /**
     * MultiTypeaheadSelect reads its selection off the `selected` flag of its options, so
     * the flags are recomputed from the edit buffer rather than from the saved value. That
     * keeps the chips in the toggle in step with what Confirm would write, and lets Cancel
     * throw the whole set away.
     */
    const multiOptions = React.useMemo(() => {
        if (type !== 'MultiFilterableSelect') return [];
        const selectedValues: (string | number)[] = Array.isArray(inputValue) ? inputValue : [];
        return (options as MultiTypeaheadSelectOption[]).map(o => ({ ...o, selected: selectedValues.includes(o.value) }));
    }, [type, options, inputValue]);

    /**
     * The chosen option, or options, as plain text.
     *
     * For a disabled select, which is rendered as a disabled TextInput holding this rather than as
     * the select itself: PatternFly's typeaheads disable their toggle but not the input inside it,
     * so a disabled one still takes typing - it cannot commit what is typed, but it shows it, and a
     * field that displays what it will not keep is worse than one that cannot be typed in at all.
     */
    const selectedOptionText = (): string => (options as TypeaheadSelectOption[])
        .filter(o => (Array.isArray(inputValue) ? inputValue.includes(o.value) : o.value === inputValue))
        .map(o => String(o.content ?? ''))
        .join(', ');

    const renderInputComponent = () => {
        switch (type) {
            case 'TextInput':
                return (
                    <TextInput
                        value={inputValue as string}
                        onChange={(_e, v) => setInputValue(v)}
                        onBlur={handleOnBlur}
                        id={id}
                        isDisabled={isDisabled}
                        validated={validated}
                        {...props.textInputProps}
                    />
                );
            case 'TextArea':
                return (
                    <TextArea
                        value={inputValue as string}
                        id={id}
                        onBlur={handleOnBlur}
                        onChange={(_e, v) => setInputValue(v)}
                        isDisabled={isDisabled}
                        validated={validated}
                        {...props.textAreaProps}
                    />
                );
            case 'Select':
                return (
                    <FormSelect
                        value={inputValue as string}
                        onChange={(_e, v) => setInputValue(v)}
                        onBlur={handleOnBlur}
                        isDisabled={isDisabled}
                        validated={validated}
                        {...props.selectProps}
                    >
                        {options.map((o, i) => (
                            <FormSelectOption 
                                key={i} 
                                value={o.value} 
                                label={o.label as string} 
                                isPlaceholder={o.isPlaceholder} 
                                isDisabled={o.isDisabled} 
                            />
                        ))}
                    </FormSelect>
                );
            case 'FilterableSelect':
                if (isDisabled) return <TextInput value={selectedOptionText()} id={id} isDisabled aria-label={label} />;
                return (
                    <TypeaheadSelect
                        initialOptions={singleOptions}
                        placeholder={placeholder}
                        noOptionsFoundMessage={(filter) => `No option was found for "${filter}"`}
                        onClearSelection={() => setInputValue('')}
                        onBlur={handleOnBlur}
                        isDisabled={isDisabled}
                        toggleProps={{ status: toggleStatus }}
                        onSelect={(_ev, selection) => {
                            setInputValue(selection as string);
                            if (isGlobalEdition) onSave(selection as string);
                        }}
                        className="pf-u-w-100"
                    />
                );
            case 'MultiFilterableSelect':
                if (isDisabled) return <TextInput value={selectedOptionText()} id={id} isDisabled aria-label={label} />;
                return (
                    <MultiTypeaheadSelect
                        initialOptions={multiOptions}
                        placeholder={placeholder}
                        noOptionsFoundMessage={(filter) => `No option was found for "${filter}"`}
                        onBlur={handleOnBlur}
                        isDisabled={isDisabled}
                        toggleProps={{ status: toggleStatus }}
                        onSelectionChange={(_ev, selections) => {
                            setInputValue(selections);
                            if (isGlobalEdition) onSave(selections);
                        }}
                        className="pf-u-w-100"
                    />
                );
            case 'Checkbox':
                return (
                    <Checkbox
                        label={label}
                        id={id || label || 'checkbox'}
                        isChecked={getBoolean(inputValue)}
                        onBlur={handleOnBlur}
                        onChange={(_e, checked) => setInputValue(checked.toString())}
                        isDisabled={isDisabled}
                        {...props.checkboxProps}
                    />
                );
            case 'Telephone':
                return (
                    <PhoneInput
                        id={id}
                        value={inputValue as string}
                        onChange={(number) => setInputValue(number)}
                        onBlur={handleOnBlur}
                        ariaLabel={label}
                        isDisabled={isDisabled}
                    />
                );
            case 'Date':
                return (
                    // dateParse has to read what dateFormat writes, which is "dd/MM/yyyy". It used
                    // to be paired with dateParser, which splits on a hyphen and so fell through to
                    // its `new Date()` fallback for every slash-separated value - meaning a date
                    // typed by hand was silently read as today.
                    <DatePicker
                        value={inputValue as string}
                        placeholder="dd/mm/yyyy"
                        dateFormat={dateFormater}
                        dateParse={ddMMyyyyParse}
                        onBlur={handleOnBlur}
                        onChange={(_event, val) => setInputValue(val)}
                        isDisabled={isDisabled}
                    />
                );
            case 'DateTime':
                // DateTimePicker has no disabled state of its own - it is an input beside two
                // dropdowns - so a disabled one shows what it holds instead of offering it
                if (isDisabled) return <React.Fragment>{displayContent}</React.Fragment>;
                return (
                    <DateTimePicker
                        // FIXED: Reverted cast from Date to any/string to satisfy HTML constraints
                        value={inputValue as any}
                        placeholder="dd/mm/yyyy"
                        onChange={(_event, val) => setInputValue(val)}
                        onBlur={handleOnBlur}
                    />
                );
            default:
                return null;
        }
    };

    // Without a decorator a multi select has nothing readable to fall back on — `value` is a
    // list of option values — so the option contents are looked up and joined instead.
    const displayContent = decorator
        ? decorator(value)
        : type === 'MultiFilterableSelect'
            ? (options as MultiTypeaheadSelectOption[])
                .filter(o => (Array.isArray(value) ? value : []).includes(o.value))
                .map(o => o.content)
                .join(', ')
            : (value as React.ReactNode);

    // Dynamic styles based on the isCompact prop
    const containerStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: isCompact ? '2px' : '8px'
    };

    const rowStyle: React.CSSProperties = {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: isCompact ? '4px' : '16px'
    };

    const compactButtonStyle: React.CSSProperties | undefined = isCompact ? {
        padding: '0 4px',
        margin: 0,
        height: 'auto'
    } : undefined;

    return (
        <div style={containerStyle}>
            {label && 
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <strong>{label}</strong>
                </div>
            }

            {!isReadOnly && (isEditing || isGlobalEdition) ? (
                <div style={rowStyle}>
                    <div style={{ flexGrow: 1 }}>{renderInputComponent()}</div>
                    {!isGlobalEdition &&
                        <div style={{ display: 'flex', gap: isCompact ? '4px' : '8px' }}>
                            <Button 
                                variant="link" 
                                onClick={handleSave} 
                                icon={<CheckIcon />} 
                                style={compactButtonStyle} 
                            />
                            <Button 
                                variant="link" 
                                onClick={handleCancel} 
                                icon={<TimesIcon />} 
                                style={compactButtonStyle} 
                            />
                        </div>
                    }
                </div>
            ) : (
                <div style={rowStyle}>
                    <div>
                        {displayAs === 'Text' ? (
                            displayContent
                        ) : (
                            <Button 
                                variant='link' 
                                component="a" 
                                href={href} 
                                target='_blank' 
                                isInline 
                                style={{ whiteSpace: 'normal', textAlign: 'left' }}
                            >
                                {displayContent}
                            </Button>
                        )}
                    </div>
                    {!isGlobalEdition && !isReadOnly && !isDisabled &&
                        <div>
                            <Button
                                icon={<PencilAltIcon />}
                                variant="plain"
                                onClick={() => {
                                    setInputValue(value ?? emptyValueFor(type));
                                    setIsEditing(true);
                                }}
                                style={compactButtonStyle}
                             />
                        </div>
                    }
                </div>
            )}

            {validated === 'error' && errorText && (
                <HelperText>
                    <HelperTextItem variant="error" icon={<ExclamationCircleIcon />}>{errorText}</HelperTextItem>
                </HelperText>
            )}
        </div>
    );
};

export default InlineEdit;